import { useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Spinner, WarningCircle } from "@phosphor-icons/react";
import { useQueryClient } from "@tanstack/react-query";
import { t } from "i18next";
import type { SubmitHandler } from "react-hook-form";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { usersQueryKeys, useUsers } from "@/hooks";
import i18n from "@/i18n";
import type { User } from "@/types";
import { Button, errorToast, Input, Modal } from "@/ui";

const userSchema = z.object({
  first_name: z.string().min(1, i18n.t("profile.first_name_is_required")),
  last_name: z.string().min(1, i18n.t("profile.last_name_is_required")),
  email: z
    .string()
    .min(1, { message: i18n.t("auth.email_is_required") })
    .email({ message: i18n.t("auth.email_is_not_valid") }),
  country: z.string().min(1, i18n.t("profile.location_is_required")),
});

type UserFormValues = z.infer<typeof userSchema>;

export const EditUserForm = ({
  user,
  onClose,
}: {
  user: User;
  onClose: () => void;
}) => {
  const { useUpdateUser } = useUsers();
  const { mutate: updateUser, isPending: isPendingUpdate } = useUpdateUser();
  const queryClient = useQueryClient();

  // A user linked to a partner gets free access by email domain, so changing
  // their email here could silently break it — the backend blocks it too.
  const partnerNames = user.partners?.map((partner) => partner.name) ?? [];
  const isPartnerUser = partnerNames.length > 0;

  // Holds the pending form values while the admin confirms an email change.
  const [pendingChange, setPendingChange] = useState<UserFormValues | null>(
    null,
  );

  const {
    formState: { errors },
    handleSubmit,
    register,
  } = useForm<UserFormValues>({
    resolver: zodResolver(userSchema),
    defaultValues: {
      first_name: user?.first_name,
      last_name: user?.last_name,
      email: user?.email,
      country: user?.country,
    },
  });

  const doUpdate = (profile: UserFormValues) => {
    updateUser(
      { profile, userId: user.id },
      {
        onSuccess: () => {
          void queryClient.invalidateQueries({
            queryKey: usersQueryKeys.useUser(user.id),
          });
          setPendingChange(null);
          onClose();
        },
        onError: (err) => {
          errorToast(err);
          setPendingChange(null);
        },
      },
    );
  };

  const onSubmit: SubmitHandler<UserFormValues> = (profile) => {
    // Ask the admin to confirm when the email is actually being changed.
    if (!isPartnerUser && profile.email !== user.email) {
      setPendingChange(profile);
      return;
    }
    doUpdate(profile);
  };

  return (
    <>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="flex w-full flex-col gap-y-8 sm:w-[560px]"
      >
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <Input
            id="firstName"
            label={t("profile.first_name")}
            {...register("first_name")}
            error={errors.first_name?.message}
            required
          />

          <Input
            id="lastName"
            label={t("profile.last_name")}
            {...register("last_name")}
            error={errors.last_name?.message}
            required
          />

          {isPartnerUser ? (
            <div className="flex flex-col gap-y-1.5 sm:col-span-2">
              <span className="text-sm font-medium text-primary-950">
                Email
              </span>
              <div className="rounded-xl border border-warning-300 bg-warning-50 px-4 py-3">
                <p className="font-medium text-primary-950">{user.email}</p>
                <p className="mt-1 flex items-start gap-x-1.5 text-sm text-warning-800">
                  <WarningCircle
                    size={16}
                    weight="fill"
                    className="mt-0.5 shrink-0 text-warning-500"
                  />
                  <span>
                    Can&apos;t edit — this user is part of{" "}
                    <strong>{partnerNames.join(", ")}</strong>. Please contact
                    the developer to change their email.
                  </span>
                </p>
              </div>
            </div>
          ) : (
            <Input
              type="email"
              id="email"
              label="Email"
              containerClassName="sm:col-span-2"
              {...register("email")}
              error={errors.email?.message}
              required
            />
          )}

          <Input
            id="country"
            label={t("profile.location")}
            containerClassName="sm:col-span-2"
            {...register("country")}
            error={errors.country?.message}
            required
          />
        </div>

        <div className="flex justify-center gap-2">
          <Button onClick={onClose} variant="outlined" className="flex-grow">
            {t("general.cancel")}
          </Button>

          <Button
            type="submit"
            className="flex-grow"
            disabled={isPendingUpdate}
          >
            {isPendingUpdate ? (
              <Spinner className="h-5 w-5 animate-spin" />
            ) : (
              t("general.save_changes")
            )}
          </Button>
        </div>
      </form>

      <Modal
        show={pendingChange !== null}
        onClose={() => setPendingChange(null)}
        title="Confirm email change"
        className="w-full sm:w-[440px]"
      >
        <div className="flex flex-col gap-y-6">
          <p className="text-sm text-neutral-500">
            Please double-check the new email is correct — the user will use it
            to log in, and it won&apos;t need re-verifying.
          </p>

          <div className="flex flex-col gap-y-2 rounded-xl border border-neutral-200 bg-neutral-50 px-4 py-3">
            <div className="flex flex-col">
              <span className="text-xs uppercase tracking-wide text-neutral-400">
                Current
              </span>
              <span className="text-neutral-500 line-through">
                {user.email}
              </span>
            </div>
            <div className="flex flex-col">
              <span className="text-xs uppercase tracking-wide text-neutral-400">
                New
              </span>
              <span className="font-medium text-primary-950">
                {pendingChange?.email}
              </span>
            </div>
          </div>

          <div className="flex justify-center gap-2">
            <Button
              variant="outlined"
              className="flex-grow"
              onClick={() => setPendingChange(null)}
            >
              {t("general.cancel")}
            </Button>
            <Button
              className="flex-grow"
              disabled={isPendingUpdate}
              onClick={() => pendingChange && doUpdate(pendingChange)}
            >
              {isPendingUpdate ? (
                <Spinner className="h-5 w-5 animate-spin" />
              ) : (
                "Confirm change"
              )}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};
